import styled from "styled-components";
import { ButtonAppearance, ButtonVariant, ButtonSize } from "./button.types";
import {
  colors,
  spacing,
  textStyles,
  radius,
  opacity,
  toCSS
} from "design-system/foundation";

const getBackground = (
  appearance: ButtonAppearance,
  variant: ButtonVariant
) => {
  if (variant === "ghost") {
    return `background: transparent;`;
  }

  if (appearance === "general") {
    if (variant === "primary") {
      return `
        background: ${colors.bg.brand.primaryBold};
        &:hover { background: ${colors.bg.brand.secondaryBold}; }
        &:active { background: ${colors.bg.brand.secondaryBold}; }
      `;
    } else {
      return `
        background: ${colors.bg.information.primarySoft};
        &:hover { background: ${colors.bg.information.secondarySoft}; }
        &:active { background: ${colors.bg.information.secondarySoft}; }
      `;
    }
  } else if (appearance === "destructive") {
    if (variant === "primary") {
      return `
        background: ${colors.bg.error.primaryBold};
        &:hover { background: ${colors.bg.error.secondaryBold}; }
        &:active { background: ${colors.bg.error.secondaryBold}; }
        `;
    } else {
      return `
        background: ${colors.bg.error.primarySoft};
        &:hover { background: ${colors.bg.error.secondarySoft}; }
        &:active { background: ${colors.bg.error.secondarySoft}; }
        `;
    }
  } else {
    if (variant === "primary") {
      return `
        background: ${colors.bg.invert.primaryBold};
        &:hover { background: ${colors.bg.invert.secondaryBold}; }
        &:active { background: ${colors.bg.invert.secondaryBold}; }
        `;
    } else {
      return `
        background: ${colors.bg.invert.primarySoft};
        &:hover { background: ${colors.bg.invert.secondarySoft}; }
        &:active { background: ${colors.bg.invert.secondarySoft}; }
        `;
    }
  }
};

const getColor = (appearance: ButtonAppearance, variant: ButtonVariant) => {
  if (appearance === "general") {
    if (variant === "primary") {
      return `color: ${colors.text.state.invertDefault};`;
    } else if (variant === "secondary") {
      return `color: ${colors.text.state.brandDefault};`;
    } else {
      return `
        color: ${colors.text.state.brandDefault};
        &:hover { color: ${colors.text.state.brandHover}; }
        &:active { color: ${colors.text.state.brandPress}; }
      `;
    }
  } else if (appearance === "destructive") {
    if (variant === "primary") {
      return `color: ${colors.text.state.invertDefault};`;
    } else if (variant === "secondary") {
      return `color: ${colors.text.state.destructiveDefault};`;
    } else {
      return `
        color: ${colors.text.state.destructiveDefault};
        &:hover { color: ${colors.text.state.destructiveHover}; }
        &:active { color: ${colors.text.state.destructivePress}; }
      `;
    }
  } else {
    if (variant === "primary") {
      return `color: ${colors.text.state.brandDefault};`;
    } else if (variant === "secondary") {
      return `color: ${colors.text.state.invertDefault};`;
    } else {
      return `
        color: ${colors.text.state.invertDefault};
        &:hover { color: ${colors.text.state.invertHover}; }
        &:active { color: ${colors.text.state.invertPress}; }
      `;
    }
  }
};

const getIconStyles = (
  appearance: ButtonAppearance,
  variant: ButtonVariant
) => {
  let defaultColor;

  if (appearance === "general") {
    defaultColor =
      variant === "primary"
        ? colors.icon.state.invertDefault
        : colors.icon.state.brandDefault;
  } else if (appearance === "destructive") {
    defaultColor =
      variant === "primary"
        ? colors.icon.state.invertDefault
        : colors.icon.state.destructiveDefault;
  } else {
    defaultColor =
      variant === "primary"
        ? colors.icon.state.brandDefault
        : colors.icon.state.invertDefault;
  }

  if (variant !== "ghost") {
    return `.icon { color: ${defaultColor}; }`;
  }

  const hoverColor =
    appearance === "general"
      ? colors.icon.state.brandHover
      : appearance === "destructive"
        ? colors.icon.state.destructiveHover
        : colors.icon.state.invertHover;
  const pressColor =
    appearance === "general"
      ? colors.icon.state.brandPress
      : appearance === "destructive"
        ? colors.icon.state.destructivePress
        : colors.icon.state.invertPress;

  return `
    .icon { color: ${defaultColor}; }
    &:hover .icon { color: ${hoverColor}; }
    &:active .icon { color: ${pressColor}; }
  `;
};

const getPadding = (size: ButtonSize) => {
  if (size === "small") return `padding: ${spacing.sm} ${spacing.xl};`;
  if (size === "medium") return `padding: ${spacing.md} ${spacing["2xl"]};`;
  return `padding: ${spacing.xl} ${spacing["4xl"]};`;
};

export const ButtonContainer = styled.button<{
  $variant: ButtonVariant;
  $appearance: ButtonAppearance;
  $size: ButtonSize;
  $isFullWidth?: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  gap: ${spacing.md};
  border: none;
  border-radius: ${radius.xs};
  cursor: pointer;
  transition: transform 0.1s ease;
  &:active {
    transform: scale(0.95);
  }
  ${({ $size }) => {
    if ($size === "small") return toCSS(textStyles.caption.caption7);
    if ($size === "medium") return toCSS(textStyles.caption.caption6);
    return toCSS(textStyles.caption.caption5);
  }}
  ${({ $appearance, $variant }) => getBackground($appearance, $variant)}
  ${({ $appearance, $variant }) => getColor($appearance, $variant)}
  ${({ $size }) => getPadding($size)}
  ${({ $isFullWidth }) => $isFullWidth && `width: 100%;`}
  ${({ $disabled }) =>
    $disabled && `opacity: ${opacity.disabled}; cursor: not-allowed;`}

  .icon {
    display: flex;
    align-items: center;
    ${({ $size }) => {
      if ($size === "small")
        return `font-size: ${textStyles.caption.caption7.lineHeight};`;
      if ($size === "medium")
        return `font-size: ${textStyles.caption.caption6.lineHeight};`;
      return `font-size: ${textStyles.caption.caption5.lineHeight};`;
    }}
  }

  ${({ $appearance, $variant }) => getIconStyles($appearance, $variant)}
`;
